/**
 * LYT Communications - Offline Queue Service
 *
 * localStorage-based action queue for offline field use.
 * Queues note replies and status transitions, then flushes
 * directly to API endpoints when connectivity returns.
 */

const QUEUE_KEY = 'lyt-offline-queue';
const NOTES_PREFIX = 'lyt-offline-notes-';

// --- Queue helpers ---

export function getQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY)) || [];
  } catch {
    return [];
  }
}

function saveQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (err) {
    console.error('[offlineQueue] Failed to save queue:', err);
  }
}

export function enqueueAction(type, payload) {
  const queue = getQueue();
  queue.push({
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type,       // 'reply' | 'transition'
    payload,
    timestamp: Date.now(),
  });
  saveQueue(queue);
  return queue.length;
}

export function getQueueCount() {
  return getQueue().length;
}

export function clearQueue() {
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch {}
}

// --- Flush queue (send each action to API) ---

export async function flushQueue() {
  const queue = getQueue();
  if (queue.length === 0) return { sent: 0, failed: 0 };

  let sent = 0;
  let failed = 0;
  const remaining = [];

  for (const item of queue) {
    try {
      let resp;
      if (item.type === 'reply') {
        resp = await fetch('/api/note-replies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.payload),
        });
      } else if (item.type === 'transition') {
        resp = await fetch('/api/map-notes', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.payload),
        });
      } else if (item.type === 'status_update') {
        resp = await fetch('/api/status-changes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.payload),
        });
      } else {
        // Unknown type — discard
        sent++;
        continue;
      }

      const json = await resp.json();
      if (json.success) {
        sent++;
      } else {
        console.warn('[offlineQueue] API rejected item:', json.error, item);
        remaining.push(item);
        failed++;
      }
    } catch (err) {
      // Network still down or server error — keep in queue
      console.warn('[offlineQueue] Flush failed for item:', err.message);
      remaining.push(item);
      failed++;
    }
  }

  saveQueue(remaining);
  return { sent, failed };
}

// --- Notes cache (for offline page refresh) ---

export function cacheNotes(projectId, notes) {
  try {
    localStorage.setItem(NOTES_PREFIX + projectId, JSON.stringify(notes));
  } catch {}
}

export function getCachedNotes(projectId) {
  try {
    return JSON.parse(localStorage.getItem(NOTES_PREFIX + projectId)) || [];
  } catch {
    return [];
  }
}
